import { createContext, useContext, useEffect, useState } from 'react'
import type { User, Session } from '@supabase/supabase-js'
import { supabase } from './supabase'
import { setSentryUser, clearSentryUser } from './sentry'
import { isCinqoStaff, bootIntercom, shutdownIntercom } from './intercom'

/* ------------------------------------------------------------------ */
/*  Auth context                                                       */
/* ------------------------------------------------------------------ */

interface AuthState {
  user: User | null
  session: Session | null
  loading: boolean
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Dev autologin : bypass Google OAuth en dev local
    const devEmail = import.meta.env.VITE_DEV_AUTOLOGIN_EMAIL as string | undefined
    const devPassword = import.meta.env.VITE_DEV_AUTOLOGIN_PASSWORD as string | undefined

    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session && devEmail && devPassword && import.meta.env.DEV) {
        console.log('[auth] dev autologin →', devEmail)
        const { error } = await supabase.auth.signInWithPassword({
          email: devEmail,
          password: devPassword,
        })
        if (error) console.warn('[auth] dev autologin failed:', error.message)
        // onAuthStateChange se charge du reste
        return
      }
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session)
      // Garder la MEME reference user tant que l'id ne change pas. Sinon TOKEN_REFRESHED
      // (emis au retour de focus sur l'onglet) cree un nouvel objet user -> relance les
      // effets [user] (ex. resolution du role dans AppContent) -> flash PageLoader ->
      // REMONTE le portail -> l'utilisateur perd sa page (retour accueil) et le setup
      // in-page (lignes togglees, etc.). Cf bug partenaire 2026-06-22.
      setUser(prev => (prev?.id === session?.user?.id ? prev : (session?.user ?? null)))
      setLoading(false)

      // Auth V2 (audit 2026-05-03) : check post-OAuth Google.
      // Google Workspace Internal devrait deja rejeter les non-@cinqo.fr cote
      // GCP Console, ce check est defense in depth si OAuth client encore en
      // "External" ou si bypass.
      if (event === 'SIGNED_IN' && session?.user) {
        const provider = (session.user.app_metadata as { provider?: string } | null)?.provider
        const email = session.user.email?.toLowerCase() ?? ''
        if (provider === 'google' && !email.endsWith('@cinqo.fr')) {
          console.warn('[auth-v2] Google OAuth rejected for non-cinqo.fr email:', email)
          void supabase.auth.signOut().then(() => {
            alert("La connexion Google est reservee au staff Cinqo (@cinqo.fr). Merci d'utiliser email + mot de passe.")
          })
          return
        }
      }

      // Sentry user context
      if (session?.user) {
        setSentryUser({ id: session.user.id, email: session.user.email })
      } else {
        clearSentryUser()
      }

      // Boot Intercom with user identity (pas pour l'equipe Cinqo, cf lib/intercom)
      if (session?.user && !isCinqoStaff(session.user)) {
        bootIntercom(session.user)
      } else {
        shutdownIntercom()
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const signInWithGoogle = async () => {
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
        queryParams: { hd: 'cinqo.fr' },
      },
    })
  }

  const signOut = async () => {
    await supabase.auth.signOut()
  }

  return (
    <AuthContext.Provider value={{ user, session, loading, signInWithGoogle, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
